import { useEffect } from 'react';

/**
 * Release the dashboard's scroll cage for a standalone public page.
 *
 * `index.css` locks `html`, `body` and `#root` to `height: 100%` +
 * `overflow: hidden` because the dashboard scrolls in inner containers. A
 * standalone public page (tip, invoice, payment link) has no such container, so
 * the DOCUMENT has to scroll. While that cage is in place, anything taller than
 * the viewport is clipped outright, and patching around it with a nested
 * `h-full overflow-y-auto` scroller only half-works: a nested scroller answers
 * the wheel under the pointer, but there is no document scrollbar to say the
 * page got longer, and Space / PageDown / End still drive the document — which
 * cannot move. That is exactly the tip-page report: the payment form mounts,
 * the submit button lands below the fold, and the page looks stuck.
 *
 * So: drop the height lock and the overflow lock for as long as a public shell
 * is mounted, and restore the previous inline values on the way out (the
 * dashboard's own rules come straight back from the stylesheet).
 *
 * Refcounted, because more than one shell can be mounted across a route
 * transition (and React StrictMode mounts each of them twice in dev): the cage
 * is released once on the first mount and restored once on the last unmount,
 * always from the values captured before anything was touched.
 */

let releaseDepth = 0;
let restoreStyles: { el: HTMLElement; height: string; overflow: string }[] = [];

function cagedElements(): HTMLElement[] {
  const root = document.getElementById('root');
  return root
    ? [document.documentElement, document.body, root]
    : [document.documentElement, document.body];
}

export function usePublicPageScroll(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    if (releaseDepth === 0) {
      restoreStyles = cagedElements().map((el) => ({
        el,
        height: el.style.height,
        overflow: el.style.overflow,
      }));
      for (const { el } of restoreStyles) {
        el.style.height = 'auto';
        el.style.overflow = 'visible';
      }
    }
    releaseDepth += 1;

    return () => {
      releaseDepth -= 1;
      if (releaseDepth > 0) return;
      for (const { el, height, overflow } of restoreStyles) {
        el.style.height = height;
        el.style.overflow = overflow;
      }
      restoreStyles = [];
    };
  }, [enabled]);
}
